import { Lender } from '@1delta/lender-registry'
import { Chain } from '@1delta/chain-registry'
import { AdditionalYields, GenericTokenList } from '../../../types'
import { CompoundV2GeneralPublicResponse } from '../../../types/lender/compound-v2-types'
import {
  getCompoundV2Tokens,
  getCompoundV2Comptroller,
  getCompoundV2Lens,
} from '../addresses/compoundV2'
import { readVTokenMetadata } from './getters/venus'
import {
  isBenqiType,
  isKineticType,
  isSumerType,
  isTectonicType,
  isVenusType,
} from '../../../utils'
import { parseMoonwellMarket } from './getters/moonwell'
import {
  readSumerCTokenMetadata,
  parseSumerAssetGroup,
  normalizeSumerGroups,
  SUMER_GROUP_COUNT,
} from './getters/sumer'
import { parseTakaraMarketsInfo } from './getters/takara'
import { parseTectonicMarketData } from './getters/tectonic'
import { parseKineticMarketMetadata } from './getters/kinetic'
import { CTokenMeta, CTokenReader } from './convert/types'
import { convertSingleEntry } from './convert/standard'
import { convertSumerEntry } from './convert/sumer'
import { convertTakaraEntry } from './convert/takara'
import { convertTectonicEntry } from './convert/tectonic'
import { convertBenqiEntry } from './convert/benqi'
import { convertKineticEntry } from './convert/kinetic'
import {
  parseBenqiMarketMetadata,
  parseBenqiEcoMarketMetadata,
} from './getters/benqi'
import {
  readLodestarCTokenMetadata,
  readCompoundV2CTokenMetadata,
  readCreamV2CTokenMetadata,
} from './getters/lodestar'
import {
  parsePausedFlag,
  resolveMarketPause,
  usesComptrollerPauseReads,
} from './pause'
import { isFailedCall } from '../../../utils/multicall'

// Parse a WAD-scaled (1e18) mantissa returned by a multicall. Reverted calls
// surface as the '0x' placeholder, in which case we return undefined so the
// caller can fall back to a default.
const parseMantissaWad = (raw: any): number | undefined => {
  if (raw === undefined || raw === null || raw === '0x') return undefined
  try {
    return Number(raw) / 1e18
  } catch {
    return undefined
  }
}

export const getCompoundV2DataConverter = (
  lender: string,
  chainId: string,
  prices: { [asset: string]: number },
  additionalYields: AdditionalYields,
  tokenList: GenericTokenList = {},
  /**
   * Seconds represented by ONE unit of this fork's `*RatePerBlock`, resolved
   * from the chain by `rateClock.ts`. Absent = fall back to the per-chain
   * `BLOCK_TIMES` guess, which is the pre-2026-08 behaviour.
   */
  unitSeconds?: number,
): [(data: any[]) => CompoundV2GeneralPublicResponse | undefined, number] => {
  // Mirror of the builder's first guard: no lens → it emits NO calls, so this
  // must declare none. Declaring `tokenCount + tail` for a slice that was never
  // built used to shift every lender after this one in the batched multicall
  // (see `sliceLenderResults` in fetchLender.ts).
  if (!getCompoundV2Lens(chainId, lender)) return [() => undefined, 0]

  // TAKARA: single call returns all markets at once
  if (lender === Lender.TAKARA) {
    return [
      (data: any[]) => {
        if (data.length !== 1) return undefined
        const marketsArray = data[0] as any[]
        const out: CompoundV2GeneralPublicResponse = { data: {}, chainId }

        for (const entryRaw of marketsArray) {
          const info = parseTakaraMarketsInfo(entryRaw)
          const underlying = info.underlying.toLowerCase()
          const asset = tokenList[underlying]
          if (!asset) continue

          const entry = convertTakaraEntry({
            entryRaw,
            asset,
            chainId,
            lender,
            prices,
            additionalYields,
            cToken: info.token,
          })
          // @ts-ignore
          out.data[entry.marketUid] = entry
        }

        return out
      },
      1, // single call
    ]
  }

  // TECTONIC: single call returns all markets at once
  if (isTectonicType(lender)) {
    return [
      (data: any[]) => {
        if (data.length !== 1) return undefined
        const marketsArray = data[0] as any[]
        const out: CompoundV2GeneralPublicResponse = { data: {}, chainId }

        for (const entryRaw of marketsArray) {
          const info = parseTectonicMarketData(entryRaw)
          const underlying = info.tokenAddress.toLowerCase()
          const asset = tokenList[underlying]
          if (!asset) continue

          const entry = convertTectonicEntry({
            entryRaw,
            asset,
            chainId,
            lender,
            prices,
            additionalYields,
            cToken: info.ttokenAddress,
          })
          // @ts-ignore
          out.data[entry.marketUid] = entry
        }

        return out
      },
      1, // single call
    ]
  }

  // KINETIC: single call returns all markets at once for this isolated pool
  if (isKineticType(lender)) {
    return [
      (data: any[]) => {
        if (data.length !== 1) return undefined
        const marketsArray = data[0] as any[]
        const out: CompoundV2GeneralPublicResponse = { data: {}, chainId }

        for (const entryRaw of marketsArray) {
          const parsed = parseKineticMarketMetadata(entryRaw)
          const underlying = parsed.underlyingToken.toLowerCase()
          const asset = tokenList[underlying]
          if (!asset) continue

          const entry = convertKineticEntry({
            entryRaw,
            parsed,
            asset,
            chainId,
            lender,
            prices,
            additionalYields,
            cToken: parsed.market,
          })
          // @ts-ignore
          out.data[entry.marketUid] = entry
        }

        return out
      },
      1, // single call
    ]
  }

  // BENQI: single call returns all markets at once
  if (isBenqiType(lender)) {
    const parser =
      lender === Lender.BENQI_AVALANCHE_ECOSYSTEM
        ? parseBenqiEcoMarketMetadata
        : parseBenqiMarketMetadata
    return [
      (data: any[]) => {
        if (data.length !== 1) return undefined
        const marketsArray = data[0] as any[]
        const out: CompoundV2GeneralPublicResponse = { data: {}, chainId }

        for (const entryRaw of marketsArray) {
          const parsed = parser(entryRaw)
          const underlying = parsed.underlying.token.toLowerCase()
          const asset = tokenList[underlying]
          if (!asset) continue

          const entry = convertBenqiEntry({
            entryRaw,
            parsed,
            asset,
            chainId,
            lender,
            prices,
            additionalYields,
            cToken: parsed.market,
          })
          // @ts-ignore
          out.data[entry.marketUid] = entry
        }

        return out
      },
      1, // single call
    ]
  }

  const compoundV2Tokens = getCompoundV2Tokens(chainId, lender)
  const tokenCount = compoundV2Tokens.length

  // Sumer has extra calls for asset groups
  const isSumer = isSumerType(lender)
  // Standard (non-Sumer) path appends 2 comptroller reads
  // (closeFactorMantissa + liquidationIncentiveMantissa) when a comptroller is
  // known for this fork — see publicCallBuild.ts.
  const stdComptroller = isSumer
    ? undefined
    : getCompoundV2Comptroller(chainId, lender)
  const stdComptrollerCalls = stdComptroller ? 2 : 0
  // Forks whose lens omits pause flags get two extra reads per market
  // (mintGuardianPaused, borrowGuardianPaused) appended after those two.
  const pauseReads =
    Boolean(stdComptroller) && usesComptrollerPauseReads(lender)
  const pauseCalls = pauseReads ? tokenCount * 2 : 0
  const expectedNumberOfCalls = isSumer
    ? tokenCount + SUMER_GROUP_COUNT
    : tokenCount + stdComptrollerCalls + pauseCalls

  // Select reader for this lender
  const reader: CTokenReader = getReader(lender, chainId, unitSeconds)!

  return [
    (data: any[]) => {
      if (data.length !== expectedNumberOfCalls) return undefined
      const out: CompoundV2GeneralPublicResponse = { data: {}, chainId }

      // For Sumer: parse the group data from the tail of the results
      if (isSumer) {
        const groupData = data.slice(tokenCount)
        const groups = normalizeSumerGroups(groupData.map(parseSumerAssetGroup))

        for (let i = 0; i < tokenCount; i++) {
          const { underlying, cToken } = compoundV2Tokens[i]
          if (!cToken || !underlying) continue
          const asset = tokenList[underlying]
          // safe: skip if not in list
          if (!asset) continue
          const entryRaw = data[i]
          const entry = convertSumerEntry({
            entryRaw,
            asset,
            chainId,
            lender,
            prices,
            additionalYields,
            cToken,
            groups,
          })
          // @ts-ignore
          out.data[entry.marketUid] = entry
        }

        return out
      }

      // --- standard compound V2 path ---
      // The two comptroller reads (if any) are the last two entries; parse them
      // into global closeFactor / liquidation-penalty values, falling back to
      // defaults when the comptroller is unknown or the call reverted.
      let globalCloseFactor: number | undefined
      let globalLiquidationPenalty: number | undefined
      if (stdComptrollerCalls === 2) {
        const closeFactorMantissa = parseMantissaWad(data[tokenCount])
        const incentiveMantissa = parseMantissaWad(data[tokenCount + 1])
        globalCloseFactor = closeFactorMantissa
        globalLiquidationPenalty =
          incentiveMantissa !== undefined
            ? Math.max(incentiveMantissa - 1, 0)
            : undefined
      }

      // Per-market pause guardians, when this fork needs them: two grouped
      // blocks after the global reads — all mints, then all borrows. A
      // reverted read decodes to `undefined` (unknown), never to `false`.
      const mintPausedBase = tokenCount + stdComptrollerCalls
      const borrowPausedBase = mintPausedBase + tokenCount

      for (let i = 0; i < tokenCount; i++) {
        const { underlying, cToken } = compoundV2Tokens[i]
        if (!cToken || !underlying) continue
        const asset = tokenList[underlying]
        // safe: skip if not in list
        if (!asset) continue
        const entryRaw = data[i]
        /**
         * A per-market read that failed comes back as the `'0x'` placeholder,
         * and every reader indexes into it (`'cToken' in x`) — which throws on a
         * string, and the throw escapes the converter, so `getLenderPublicData`
         * drops the WHOLE lender. Two dead markets out of Cream's 47 on BNB were
         * enough to serve nothing for that venue.
         *
         * One broken market must cost one market. Skipping it here is also the
         * honest outcome: absent, not zeroed — a market published with a zero
         * exchange rate is worse than a missing one.
         */
        if (isFailedCall(entryRaw)) continue
        const entry = convertSingleEntry({
          entryRaw,
          reader,
          asset,
          chainId,
          lender,
          prices,
          additionalYields,
          cToken,
          closeFactor: globalCloseFactor,
          liquidationPenalty: globalLiquidationPenalty,
          // live guardian read first, then the static fallback published on
          // the token entry for forks that expose no getter at all
          pause: pauseReads
            ? resolveMarketPause(compoundV2Tokens[i], {
                mintPaused: parsePausedFlag(data[mintPausedBase + i]),
                borrowPaused: parsePausedFlag(data[borrowPausedBase + i]),
              })
            : undefined,
        })
        // @ts-ignore
        out.data[entry.marketUid] = entry
      }

      return out
    },
    expectedNumberOfCalls,
  ]
}

function getReader(lender: string, chainId: string, unitSeconds?: number) {
  if (isVenusType(lender)) {
    return (x: any, t: any) =>
      readVTokenMetadata(x, t, unitSeconds) as CTokenMeta
  }
  if (lender === Lender.MOONWELL) {
    return (x: any, t: any) => parseMoonwellMarket(x, t)
  }
  if (isSumerType(lender)) {
    return (x: any, t: any) => readSumerCTokenMetadata(x, t) as CTokenMeta
  }
  if (lender === Lender.LODESTAR || lender === Lender.TENDER) {
    return (x: any, t: any) =>
      readLodestarCTokenMetadata(x, t, unitSeconds) as CTokenMeta
  }
  // Cream on Polygon/Arbitrum: shifted indices (17-field V2 variant)
  if (
    lender === Lender.CREAM_FINANCE &&
    (chainId === Chain.POLYGON_MAINNET || chainId === Chain.ARBITRUM_ONE)
  ) {
    return (x: any, t: any) =>
      readCreamV2CTokenMetadata(x, t, unitSeconds) as CTokenMeta
  }
  if (
    lender === Lender.COMPOUND_V2 ||
    lender === Lender.FLUX_FINANCE ||
    lender === Lender.WE_PIGGY ||
    lender === Lender.CREAM_FINANCE ||
    lender === Lender.CAPY_FI ||
    lender === Lender.GAMMA ||
    // long-tail forks read through a shared 14-field lens — MUST mirror the
    // `CreamLensAbi` branch in publicCallBuild.ts `getCall`. A lender in that
    // branch and missing here builds fine and then dies in the converter with
    // `reader is not a function`, which is not a length mismatch and so is not
    // caught by the parser's own guard.
    lender === Lender.DEMETER ||
    lender === Lender.LANDER ||
    lender === Lender.FILDA ||
    lender === Lender.SOLIDLIZARD ||
    lender === Lender.IRON_BANK ||
    lender === Lender.MENDI ||
    lender === Lender.PAXO ||
    lender === Lender.SONNE ||
    lender === Lender.ASO_FINANCE ||
    lender === Lender.BLUME ||
    lender === Lender.KAWA ||
    lender === Lender.XPERT ||
    lender === Lender.KEOM ||
    lender === Lender.REACTOR_FUSION
  ) {
    return (x: any, t: any) =>
      readCompoundV2CTokenMetadata(x, t, unitSeconds) as CTokenMeta
  }
}
